import React, { FC } from "react";
import styled from "styled-components";

import { useLocale } from "@adsk/alloy-react-locale";
import Theme from "@adsk/alloy-react-theme";

import i18n from "../../i18n";

// `bodySmall` + charcoal700 matches the muted "N% completed" line shown
// under a status name on dense list rows (e.g. acc-viewer's
// `TrackableAssetList`) and inside the read-only asset detail panel via
// `StatusWithProgressDisplay`.
const Label = styled.span`
  ${Theme.typography.bodySmall}
  color: ${Theme.colors.charcoal700};
`;

const clampPercent = (value: number): number =>
  Math.round(Math.min(100, Math.max(0, value)));

export type PercentCompleteTextProps = {
  percent: number;
  /**
   * Forwarded to the underlying `<span>` so callers can compose via
   * styled-components (e.g. {@code styled(PercentCompleteText)}) without
   * losing the base typography.
   */
  className?: string;
  /**
   * Override the default `data-testid` so consumers that already assert a
   * stable id (e.g. `StatusWithProgressDisplay` keeps
   * `StatusPercentCompleteLabel` for backward compat) don't have to
   * re-target their tests when adopting this component.
   */
  "data-testid"?: string;
};

const PercentCompleteText: FC<PercentCompleteTextProps> = ({
  percent,
  className,
  "data-testid": dataTestId = "PercentCompleteText",
}) => {
  const { T } = useLocale(i18n);
  const safePercent = clampPercent(percent);
  return (
    <Label className={className} data-testid={dataTestId}>
      {T.translate("labels.percentCompleted", {
        percent: safePercent,
      })?.toString()}
    </Label>
  );
};

export default PercentCompleteText;
